import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { useProduct } from "@/hooks/useProduct";
import { logger } from "@/utils/logger";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft, Search, MessageSquare } from "lucide-react";
import { SEOPresets } from "@/components/SEO";

const NotFound = () => {
  const location = useLocation();
  const config = useProduct();

  useEffect(() => {
    logger.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      {SEOPresets.notFound}
      <Header showLogout solidBackground={true} />

      {/* Hero Section with consistent design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary via-accent to-primary py-24 px-4 sm:px-6 lg:px-8">
        {/* Grid pattern background */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Animated gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDuration: "4s" }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent/20 rounded-full blur-3xl animate-pulse"
            style={{ animationDuration: "6s", animationDelay: "2s" }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          {/* 404 Number with animation */}
          <div className="mb-8 animate-fade-in">
            <span className="text-[150px] sm:text-[200px] font-extrabold text-white/20 leading-none select-none">
              404
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-6 tracking-tight animate-fade-in">
            {config.i18n.notFoundPage.title}
          </h1>

          <p className="text-xl sm:text-2xl text-white/90 max-w-2xl mx-auto leading-relaxed mb-8 animate-fade-in">
            {config.i18n.notFoundPage.message}
          </p>

          <p className="text-white/70 mb-12 animate-fade-in">
            The page{" "}
            <code className="bg-white/20 px-2 py-1 rounded text-white font-mono text-sm">
              {location.pathname}
            </code>{" "}
            doesn't exist.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
            <Button
              asChild
              size="lg"
              className="bg-white text-primary hover:bg-white/90 hover:scale-105 text-lg px-8 py-6 h-auto font-bold shadow-2xl transition-transform duration-300"
            >
              <Link to="/">
                <Home className="mr-2 w-5 h-5" />
                {config.i18n.backToHome}
              </Link>
            </Button>

            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white/30 text-white hover:bg-white/10 hover:scale-105 text-lg px-8 py-6 h-auto font-bold transition-transform duration-300 bg-transparent"
            >
              <Link to="/meeting-simulator">
                <MessageSquare className="mr-2 w-5 h-5" />
                Browse Meetings
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Helpful Links Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Looking for something?
          </h2>
          <p className="text-muted-foreground">
            Here are some helpful links to get you back on track
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Home Card */}
          <Link
            to="/"
            className="group p-6 rounded-2xl border-2 border-border/50 hover:border-primary/50 bg-card hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Home className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Home</h3>
            <p className="text-muted-foreground text-sm">
              Start fresh from our homepage
            </p>
          </Link>

          {/* Meeting Simulator Card */}
          <Link
            to="/meeting-simulator"
            className="group p-6 rounded-2xl border-2 border-border/50 hover:border-primary/50 bg-card hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">
              Meeting Simulator
            </h3>
            <p className="text-muted-foreground text-sm">
              Practice your English in realistic scenarios
            </p>
          </Link>

          {/* FAQ Card */}
          <Link
            to="/faq"
            className="group p-6 rounded-2xl border-2 border-border/50 hover:border-primary/50 bg-card hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Search className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">FAQ</h3>
            <p className="text-muted-foreground text-sm">
              Find answers to common questions
            </p>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} {config.i18n.appName}. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NotFound;
